import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { PersonId } from '@shared/brand';
import type { CalendarDate } from '@domain/value-objects/CalendarDate';
import { getServices } from '@infrastructure/services';
import { livePeriodsFor, pairLiveBalances, type LiveBalance } from '../balancePeriod';

/**
 * Los saldos vigentes de una persona, del que vence antes al que vence después.
 *
 * Una sola lectura (`getMany`) para todos sus periodos vivos. La clave lleva los
 * meses de vencimiento: si Configuración los cambia, cambia qué periodos siguen
 * vivos y hay que volver a pedirlos. Empieza por `scope` para que cada pantalla
 * siga invalidando sus propias claves como antes.
 */
export const useLiveBalances = (params: {
  readonly scope: string;
  readonly personId: PersonId;
  readonly hireDate: CalendarDate | null;
  readonly expiryMonths: number;
  readonly enabled?: boolean;
}): UseQueryResult<readonly LiveBalance[]> => {
  const { scope, personId, hireDate, expiryMonths, enabled = true } = params;
  return useQuery({
    queryKey: [scope, 'balance', personId, hireDate?.toISO() ?? null, expiryMonths],
    enabled,
    queryFn: async () => {
      const today = getServices().clock.today();
      const periods = livePeriodsFor(hireDate, today, expiryMonths);
      const result = await getServices().balances.getMany(
        periods.map((period) => ({ personId, periodYear: period.year })),
      );
      if (!result.ok) {
        /*
         * Al log, no en silencio. Una lista vacía es lo que la pantalla sabe
         * dibujar —la tarjeta simplemente no sale—, y así pasó el 21 sep 2026:
         * las reglas negaban la consulta entera y nada en la consola lo decía.
         */
        getServices().logger.error('live balances read failed', {
          error: result.error,
          periods: periods.map((period) => period.year),
        });
        return [];
      }
      return pairLiveBalances(periods, result.value, personId, today);
    },
  });
};
